import { useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useHeader, useAuth } from '@/contexts';
import {
  SimplesNacionalContext,
  type EntradaSaida,
  type SimplesNacionalPermissoes,
} from '@/contexts/SimplesNacionalContext';

interface Props {
  children: ReactNode;
}

export function SimplesNacionalProvider({ children }: Props) {
  const { filtros, limparEmpresa } = useHeader();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [entradaSaida, setEntradaSaida] = useState<EntradaSaida>('entradas');
  const [reloadKey, setReloadKey] = useState(0);
  const [buscarCallback, setBuscarCallback] = useState<(() => void) | null>(null);

  const registrarBuscar = useCallback((fn: (() => void) | null) => {
    setBuscarCallback(() => fn);
  }, []);

  /*
    Ao entrar no módulo sai a empresa, porque a lista daqui é outra (só optantes), e a
    competência fica: a decisão 13 da SPEC-017 diz que ela vale até a pessoa trocar, e entrar
    no Simples não é trocar.
  */
  useEffect(() => {
    limparEmpresa();
  }, [limparEmpresa]);

  // Invalida queries de contribuintes ao sair do módulo
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ['contribuintes'] });
    };
  }, [queryClient]);

  const reload = useCallback(() => {
    setReloadKey(k => k + 1);
    queryClient.invalidateQueries({ queryKey: ['lista-documentos-simples'] });
    queryClient.invalidateQueries({ queryKey: ['lista-documentos-itens-simples'] });
    queryClient.invalidateQueries({ queryKey: ['lista-eventos-simples'] });
    queryClient.invalidateQueries({ queryKey: ['historico-upload'] });
  }, [queryClient]);

  const permissoes = useMemo<SimplesNacionalPermissoes>(
    () => ({
      podeUpload: !!user,
      podeExportar: !!user,
      podeReprocessar: !!user,
    }),
    [user]
  );

  const value = useMemo(
    () => ({
      cnpjContribuinte: filtros.empresaId,
      dataInicio: filtros.dataInicio,
      dataFim: filtros.dataFim,
      entradaSaida,
      setEntradaSaida,
      permissoes,
      reloadKey,
      reload,
      buscarHeader: buscarCallback,
      registrarBuscar,
    }),
    [
      filtros.empresaId,
      filtros.dataInicio,
      filtros.dataFim,
      entradaSaida,
      permissoes,
      reloadKey,
      reload,
      buscarCallback,
      registrarBuscar,
    ]
  );

  return (
    <SimplesNacionalContext.Provider value={value}>{children}</SimplesNacionalContext.Provider>
  );
}
